import React, { useEffect, useRef } from "react";

/**
 * 6-box OTP field that behaves like a native one: auto-advance, retype-over,
 * paste-anywhere, arrow keys, backspace-through and one-tap autofill.
 */
export function OtpInput({ value, onChange, onComplete, testIdPrefix = "otp-input", autoFocus = true, disabled }) {
  const refs = useRef([]);
  const len = value.length;

  useEffect(() => { if (autoFocus) refs.current[0]?.focus(); }, [autoFocus]);

  const commit = (next) => {
    onChange(next);
    if (next.every((d) => d !== "")) onComplete?.(next.join(""));
  };

  const setAt = (i, digits) => {
    const next = [...value];
    // typing into a filled box replaces it; a multi-digit blob spills forward
    digits.split("").forEach((d, k) => { if (i + k < len) next[i + k] = d; });
    commit(next);
    const land = Math.min(i + digits.length, len - 1);
    refs.current[land]?.focus();
    refs.current[land]?.select();
  };

  const handleChange = (i, raw) => {
    const digits = raw.replace(/\D/g, "");
    if (!digits) { const next = [...value]; next[i] = ""; return onChange(next); }
    // when the box already holds a digit, keep the newly typed one (the last char)
    setAt(i, value[i] && digits.length > 1 ? digits.slice(-1) : digits);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = [...value];
      if (next[i]) { next[i] = ""; onChange(next); return; }
      if (i > 0) { next[i - 1] = ""; onChange(next); refs.current[i - 1]?.focus(); }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus(); }
    if (e.key === "ArrowRight" && i < len - 1) { e.preventDefault(); refs.current[i + 1]?.focus(); }
  };

  const handlePaste = (i, e) => {
    const digits = (e.clipboardData.getData("text") || "").replace(/\D/g, "");
    if (!digits) return;
    e.preventDefault();
    setAt(digits.length >= len ? 0 : i, digits.slice(0, len));
  };

  return (
    <div className="flex gap-2" data-testid={`${testIdPrefix}-group`}>
      {value.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          data-testid={`${testIdPrefix}-${i}`}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          disabled={disabled}
          maxLength={len}
          value={d}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          className="w-11 h-12 flex-1 min-w-0 max-w-[52px] text-center hairline bg-transparent font-mono text-lg outline-none transition-colors focus:border-[var(--red)]"
          style={{ color: "var(--text)" }}
        />
      ))}
    </div>
  );
}
